// Desplegable de Unidad de negocio (con color) y badge para mostrarla.
import type { JSX } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api.ts";

export interface BUnit { id: string; name: string; color: string }

export function useBusinessUnits() {
  return useQuery<{ rows: BUnit[] }>({
    queryKey: ["business-units"],
    queryFn: () => api.get("/business-units"),
    staleTime: 60_000,
  });
}

/** #RRGGBB → rgba con alpha (para fondos suaves). */
function tint(hex: string, alpha: number): string {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return "transparent";
  const n = parseInt(m[1]!, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

export function BusinessUnitSelect(
  { value, onChange }: { value: string; onChange: (v: string) => void },
): JSX.Element {
  const { data } = useBusinessUnits();
  const units = data?.rows ?? [];
  const sel = units.find((u) => u.id === value);
  return (
    <div className="relative">
      <span
        className="absolute left-3 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ring-1 ring-black/10 pointer-events-none z-10"
        style={{ background: sel?.color ?? "transparent" }}
      />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input w-full pl-8"
        style={sel ? { borderLeftWidth: "4px", borderLeftColor: sel.color } : undefined}
      >
        <option value="">— Sin unidad —</option>
        {units.map((u) => (
          <option key={u.id} value={u.id} style={{ backgroundColor: tint(u.color, 0.16), color: "#111827" }}>
            {u.name}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Etiqueta coloreada de una unidad de negocio, por id. */
export function BusinessUnitBadge({ id }: { id: string }): JSX.Element {
  const { data } = useBusinessUnits();
  const u = (data?.rows ?? []).find((x) => x.id === id);
  if (!u) return <span className="text-ink-muted">—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium"
      style={{ background: tint(u.color, 0.14), color: u.color }}>
      <span className="w-2 h-2 rounded-full" style={{ background: u.color }} /> {u.name}
    </span>
  );
}
